import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Grid } from '@ltvco/refresh-lib/theme';
import { searchFormsRecyclingValidation } from '@ltvco/refresh-lib/utils';
import {
  SearchReportResponseData,
  emailSearch,
  EmailSearchParams,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  useForm,
  useMutation,
  yupResolver,
  useFeatureIsOn,
} from '@ltvco/refresh-lib/vendors';
import React, { useContext, useCallback } from 'react';

import { SearchButton } from '../components';
import { SearchProps } from '../utils';

import { EmailFormContainer, EmailSearchFieldContainer } from './components';
import { EmailInput } from './EmailInput';
import { schemaEmailSearchForm } from './utils';

export interface EmailFormData {
  email: string;
}

export const EmailSearch: React.FC<SearchProps> = ({
  isMobile,
  placeholderText,
  onSearchSuccess,
  placement = 'dashboard',
}) => {
  const {
    session: { account },
  } = useSession();
  const { logError, trackEventGA4 } = useContext(AppConfig);

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
    control,
  } = useForm<EmailFormData>({
    resolver: yupResolver(schemaEmailSearchForm),
    defaultValues: {
      email: '',
    },
  });

  const email = watch('email');
  const isInEmailPartialAddress = useFeatureIsOn('OAR-1569');
  const handleClearEmail = useCallback(() => {
    setValue('email', '');
    clearErrors('email');
  }, [setValue, clearErrors]);

  const emailQuery = useMutation<
    SearchReportResponseData,
    Error,
    EmailSearchParams
  >({
    mutationFn: emailSearch,
    onSuccess: (data, variables) => {
      onSearchSuccess &&
        onSearchSuccess({
          permalink: data.report.permalink,
          reportType: 'email',
          searchParams: `email=${encodeURIComponent(variables.email)}`,
        });
    },
    onError: (error) => {
      logError('Email Search', error);
    },
  });

  const onEmailSearchSubmit = useCallback(
    ({ email }: { email: string }) => {
      if (!searchFormsRecyclingValidation(account)) {
        return;
      }

      trackEventGA4({
        event_name: 'report_search',
        report_type: 'email',
        search_by: 'email',
        search_from: `${placement}_search`,
      });

      emailQuery.mutate({
        email,
        showPartialAddresses: isInEmailPartialAddress,
      });
    },
    [account, trackEventGA4, emailQuery]
  );

  const renderMobileForm = useCallback(() => {
    return (
      <Grid container sx={{ mt: 2.5, mb: 2.5 }} gap={3}>
        <EmailSearchFieldContainer className={errors.email ? 'error' : ''}>
          <EmailInput
            register={register}
            placeholder={placeholderText}
            errors={errors}
            clearErrors={(field?: string) => clearErrors(field as 'email')}
            onClear={handleClearEmail}
            value={email}
            control={control}
          />
        </EmailSearchFieldContainer>

        <SearchButton type="submit" fullWidth>
          Search
        </SearchButton>
      </Grid>
    );
  }, [register, placeholderText, email, errors, clearErrors, handleClearEmail]);

  const renderDesktopForm = useCallback(() => {
    return (
      <EmailFormContainer>
        <Box flexGrow={1}>
          <EmailSearchFieldContainer
            container
            className={errors.email ? 'error' : ''}
          >
            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <EmailInput
                register={register}
                placeholder={placeholderText}
                errors={errors}
                clearErrors={(field?: string) => clearErrors(field as 'email')}
                onClear={handleClearEmail}
                value={email}
                control={control}
              />
            </Grid>
          </EmailSearchFieldContainer>
        </Box>
        <SearchButton type="submit">Search</SearchButton>
      </EmailFormContainer>
    );
  }, [register, placeholderText, email, errors, clearErrors, handleClearEmail]);

  return (
    <form onSubmit={handleSubmit(onEmailSearchSubmit)}>
      {isMobile ? renderMobileForm() : renderDesktopForm()}
    </form>
  );
};
